// @plasser/ui — StatusTransitionControl: render the allowed next-status actions
// for the current status, driven entirely by a transition config. Generic over
// the status string type so the host owns the domain (e.g. defect_status).

import * as React from "react";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../lib/utils";

export interface StatusTransition<S extends string> {
  to: S;
  label: string;
  /** Maps to a Button variant. */
  tone?: "default" | "secondary" | "destructive" | "ghost" | "action";
  /** Disable this specific transition (e.g. unmet precondition). */
  disabled?: boolean;
}

/** from-status -> allowed transitions. */
export type TransitionConfig<S extends string> = Partial<Record<S, StatusTransition<S>[]>>;

export interface StatusTransitionControlProps<S extends string> {
  status: S;
  config: TransitionConfig<S>;
  onTransition: (to: S) => void;
  /** Human label for the current status (defaults to the raw value). */
  statusLabel?: (status: S) => React.ReactNode;
  /** Copy for the terminal-state hint (default German). */
  noTransitionsLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function StatusTransitionControl<S extends string>({
  status,
  config,
  onTransition,
  statusLabel,
  noTransitionsLabel = "Keine weiteren Schritte",
  disabled,
  className,
}: StatusTransitionControlProps<S>): React.ReactElement {
  const transitions = config[status] ?? [];
  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      data-testid="status-transition-control"
    >
      <Badge variant="secondary" data-testid="status-current">
        {statusLabel ? statusLabel(status) : status}
      </Badge>
      {transitions.length === 0 ? (
        <span className="text-xs text-muted-foreground" data-testid="status-no-transitions">
          {noTransitionsLabel}
        </span>
      ) : (
        transitions.map((t) => (
          <Button
            key={t.to}
            type="button"
            size="sm"
            variant={t.tone ?? "secondary"}
            disabled={disabled || t.disabled}
            data-testid={`status-transition-${t.to}`}
            onClick={() => onTransition(t.to)}
          >
            {t.label}
          </Button>
        ))
      )}
    </div>
  );
}
